import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { fetchData } from '../../helpers/apiUtils';
import { formatNumber } from '../../helpers/formatNumber';

/**
 * Betreff-A/B (FT-Heat-Ups): je Paar Arm A gegen Arm B. Daten und Regeln kommen komplett vom
 * Endpunkt (services/WebinarSubjectAbEvaluationService.ts): Signifikanz nur je Paar,
 * Schiedsrichter Klickrate, Reife 72 h, Mindestgroesse je Arm. Der Block erscheint erst, wenn es
 * mindestens ein Paar gibt.
 */

interface Intervall {
  anteil: number | null;
  unten: number | null;
  oben: number | null;
}
interface Vergleich {
  differenz: number | null;
  zWert: number | null;
  pWert: number | null;
  signifikant: boolean;
  urteil: string;
}
interface ArmView {
  arm: 'A' | 'B';
  schedule_id: number;
  esp_campaign_id: string | null;
  status: string;
  sent_at: string | null;
  subject: string | null;
  recipients: number | null;
  opens: number | null;
  clicks: number | null;
  registrations: number | null;
  oeffnungsrate: Intervall;
  klickrate: Intervall;
  anmelderrate: Intervall;
}
interface PairView {
  mailing_email_id: number;
  mailing_number: number;
  campaign_id: number;
  campaign_name: string | null;
  webinar_id: string | null;
  webinar_title: string | null;
  scheduled_datetime: string | null;
  a: ArmView;
  b: ArmView;
  gruende: string[];
  reif: boolean;
  kein_test: boolean;
  ueberschneidung: number | null;
  balance: { anteilA: number | null; abweichungSd: number | null; auffaellig: boolean };
  klickrate: Vergleich | null;
  oeffnungsrate: Vergleich | null;
  anmelderrate: Vergleich | null;
}
interface Evaluation {
  paare: PairView[];
  gesamt: {
    paare: number;
    getestet: number;
    recipients_a: number;
    recipients_b: number;
    opens_a: number;
    opens_b: number;
    clicks_a: number;
    clicks_b: number;
    registrations_a: number;
    registrations_b: number;
  };
  regeln: { reife_stunden: number; min_empfaenger: number; schiedsrichter: string };
}

const pct = (v: number | null | undefined, digits = 1): string =>
  v === null || v === undefined ? '–' : `${(v * 100).toFixed(digits).replace('.', ',')} %`;
const perMille = (v: number | null | undefined): string =>
  v === null || v === undefined ? '–' : (v * 1000).toFixed(1).replace('.', ',');
const ci = (i: Intervall): string =>
  i.unten === null || i.oben === null ? '' : ` (${pct(i.unten)}–${pct(i.oben)})`;
const rate = (k: number, n: number): number | null => (n > 0 ? k / n : null);

const headSx = { fontWeight: 'bold', whiteSpace: 'nowrap' as const };

const SubjectAbBlock: React.FC = () => {
  const [data, setData] = useState<Evaluation | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetchData<Evaluation>('/api/webinar-mailings/evaluation/subject-ab');
        if (!alive) return;
        if (res.success && res.data) setData(res.data);
        else setError(res.error || 'Betreff-A/B konnte nicht geladen werden');
      } catch (e) {
        if (alive) setError('Betreff-A/B konnte nicht geladen werden');
        console.error(e);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (error) {
    return (
      <Alert severity="warning" sx={{ mt: 4 }}>
        {error}
      </Alert>
    );
  }
  if (!data || data.paare.length === 0) return null;

  const g = data.gesamt;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        Betreff-A/B (FT-Heat-Ups)
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Je Welle zwei Hälften mit eigenem Betreff. Urteil nur je Paar, Schiedsrichter Klickrate
        (Öffnungen sind bei Iterable MPP-verzerrt); Reife {data.regeln.reife_stunden} h, mindestens{' '}
        {formatNumber(data.regeln.min_empfaenger, 0)} Empfänger je Arm. Die Gesamtzeile ist nur eine
        Summe — dieselben Personen sitzen in jeder Welle im selben Arm, ein Test darüber wäre nicht
        gültig.
      </Typography>
      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>Welle</TableCell>
              <TableCell sx={headSx}>Arm</TableCell>
              <TableCell sx={headSx}>Betreff</TableCell>
              <TableCell sx={headSx} align="right">
                Empfänger
              </TableCell>
              <TableCell sx={headSx} align="right">
                Öffnungen
              </TableCell>
              <TableCell sx={headSx} align="right">
                Klicks
              </TableCell>
              <TableCell sx={headSx} align="right">
                Anmelder je 1.000
              </TableCell>
              <TableCell sx={headSx}>Urteil (Klickrate)</TableCell>
              <TableCell sx={headSx}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.paare.map((p) => {
              const arms: ArmView[] = [p.a, p.b];
              const leader =
                p.klickrate && p.klickrate.signifikant && p.klickrate.differenz !== null
                  ? p.klickrate.differenz > 0
                    ? 'A'
                    : 'B'
                  : null;
              return arms.map((arm, idx) => (
                <TableRow key={`${p.mailing_email_id}-${arm.arm}`} hover>
                  {idx === 0 && (
                    <TableCell rowSpan={2} sx={{ verticalAlign: 'top' }}>
                      <Typography variant="body2" fontWeight="medium">
                        {p.webinar_title || p.webinar_id || p.campaign_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Mail #{p.mailing_number} · {p.scheduled_datetime?.slice(0, 16) || ''}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: leader === arm.arm ? 'bold' : 'normal' }}>
                    {arm.arm}
                    {leader === arm.arm ? ' ▲' : ''}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>
                    <Typography
                      variant="body2"
                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={arm.subject || ''}
                    >
                      {arm.subject || '–'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {arm.recipients === null ? '–' : formatNumber(arm.recipients, 0)}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={`Wilson 95 %${ci(arm.oeffnungsrate)}`}>
                      <span>{pct(arm.oeffnungsrate.anteil)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={`Wilson 95 %${ci(arm.klickrate)}`}>
                      <span>{pct(arm.klickrate.anteil, 2)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={`Wilson 95 %${ci(arm.anmelderrate)}`}>
                      <span>{perMille(arm.anmelderrate.anteil)}</span>
                    </Tooltip>
                  </TableCell>
                  {idx === 0 && (
                    <TableCell rowSpan={2} sx={{ verticalAlign: 'top', maxWidth: 260 }}>
                      {p.klickrate ? (
                        <>
                          <Typography
                            variant="body2"
                            color={p.klickrate.signifikant ? 'success.main' : 'text.secondary'}
                          >
                            {p.klickrate.urteil}
                          </Typography>
                          {p.oeffnungsrate && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Öffnungen: {p.oeffnungsrate.urteil}
                            </Typography>
                          )}
                          {p.anmelderrate && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Anmelder: {p.anmelderrate.urteil}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          kein Urteil
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  {idx === 0 && (
                    <TableCell rowSpan={2} sx={{ verticalAlign: 'top' }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {p.gruende.length === 0 && (
                          <Chip size="small" color="success" label="getestet" />
                        )}
                        {p.gruende.map((gr) => (
                          <Chip key={gr} size="small" variant="outlined" label={gr} />
                        ))}
                        {p.balance.auffaellig && (
                          <Chip
                            size="small"
                            color="warning"
                            label={`Balance ${pct(p.balance.anteilA)} A`}
                          />
                        )}
                        <Chip
                          size="small"
                          variant="outlined"
                          color={
                            p.ueberschneidung === null
                              ? 'default'
                              : p.ueberschneidung === 0
                                ? 'success'
                                : 'error'
                          }
                          label={
                            p.ueberschneidung === null
                              ? 'Überschneidung unbekannt'
                              : `Überschneidung ${formatNumber(p.ueberschneidung, 0)}`
                          }
                          title="Gemeinsame Empfänger beider Arme laut Send-Spiegel — muss 0 sein"
                        />
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ));
            })}
            <TableRow sx={{ backgroundColor: '#f7f7f7' }}>
              <TableCell sx={headSx}>
                Summe ({g.paare} Paare, {g.getestet} getestet)
              </TableCell>
              <TableCell sx={headSx}>A / B</TableCell>
              <TableCell />
              <TableCell align="right">
                {formatNumber(g.recipients_a, 0)} / {formatNumber(g.recipients_b, 0)}
              </TableCell>
              <TableCell align="right">
                {pct(rate(g.opens_a, g.recipients_a))} / {pct(rate(g.opens_b, g.recipients_b))}
              </TableCell>
              <TableCell align="right">
                {pct(rate(g.clicks_a, g.recipients_a), 2)} /{' '}
                {pct(rate(g.clicks_b, g.recipients_b), 2)}
              </TableCell>
              <TableCell align="right">
                {perMille(rate(g.registrations_a, g.recipients_a))} /{' '}
                {perMille(rate(g.registrations_b, g.recipients_b))}
              </TableCell>
              <TableCell colSpan={2}>
                <Typography variant="caption" color="text.secondary">
                  nur Summe, kein Test
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SubjectAbBlock;
